// =============================================================================
// VandieZ admin-web -- Route & toll master (/masterdata/routes)
// =============================================================================
// C-25 (p14): "City, district, ROUTE, TOLL, pricing zone, vehicle capacity
// and status codes should be standardized across mobile, web and admin
// panel." Migration 0109 added the table and the API; this is the panel half,
// without which "controlled from the admin panel" is not true.
//
// Two things a reader should know before editing this page:
//
//   * A route is DIRECTIONAL. (A->B) and (B->A) are separate rows, because a
//     loaded run out and an empty backhaul are different commercial
//     propositions. The form says so, because an operator who assumes
//     symmetry will price half their lanes wrong.
//
//   * `standard_distance_km` is the agreed COMMERCIAL distance, NOT a
//     replacement for the routing service. The service still answers "how far
//     is this particular job"; this is what a rate card or a dispute is
//     settled against. See migration 0109 for why it does not override.
//
// Endpoints are set at creation and never editable -- repointing a lane would
// silently re-attribute every load already stamped with its id, so last
// quarter's reporting would quietly change. Retire and recreate instead,
// which the partial unique index (active pairs only) is built to allow.
//
// Follows LocationsPage's shape: same masterdataAdminApi, same soft-toggle
// instead of a hard delete, same page-level Alert.
// =============================================================================

import { AlertCircle, Loader2, Plus } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { masterdataAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  Location,
  MasterRoute,
  RouteCreatePayload,
} from "@/types/masterdata";

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "ROUTE_CODE_DUPLICATE") {
      return "That route code is already in use.";
    }
    if (e.code === "ROUTE_PAIR_EXISTS") {
      // Names the fix: the unique index is partial on is_active, so
      // retiring the old lane frees the pair without renaming anything.
      return (
        "An active route already exists between those two locations. " +
        "Deactivate it first, then create the replacement."
      );
    }
    if (e.code === "ROUTE_SAME_ENDPOINTS") {
      return "A route must run between two different locations.";
    }
    if (e.code === "ROUTE_LOCATION_NOT_FOUND") {
      return "Origin or destination location no longer exists.";
    }
    return e.problem.detail;
  }
  return "Unexpected error.";
}

interface CreateRouteFormProps {
  locations: Location[];
  onCreated: () => void;
  onCancel: () => void;
}

function CreateRouteForm({
  locations,
  onCreated,
  onCancel,
}: CreateRouteFormProps): React.JSX.Element {
  const { accessToken } = useAuth();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [originId, setOriginId] = useState("");
  const [destId, setDestId] = useState("");
  const [distanceKm, setDistanceKm] = useState("");
  const [tollInr, setTollInr] = useState("0");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (accessToken === null) return;
    setError(null);

    if (originId.length === 0 || destId.length === 0) {
      setError("Pick both an origin and a destination.");
      return;
    }
    if (originId === destId) {
      // Caught here as well as server-side: a same-endpoint lane would
      // match every single-district load if anything ever resolved by
      // district pair, and the operator should not need a round-trip to
      // learn that.
      setError("A route must run between two different locations.");
      return;
    }
    const km = Number(distanceKm);
    if (!Number.isFinite(km) || km <= 0) {
      setError("Enter a distance greater than zero.");
      return;
    }
    const toll = tollInr.trim().length === 0 ? 0 : Number(tollInr);
    if (!Number.isFinite(toll) || toll < 0) {
      // Zero is ordinary -- a toll-free lane. Negative is a typo that
      // would quietly reduce a quote.
      setError("Toll cannot be negative. Use 0 for a toll-free lane.");
      return;
    }

    setSubmitting(true);
    try {
      const payload: RouteCreatePayload = {
        code: code.trim(),
        name: name.trim(),
        origin_location_id: originId,
        dest_location_id: destId,
        standard_distance_km: km,
        toll_inr: toll,
      };
      if (notes.trim().length > 0) payload.notes = notes.trim();
      await masterdataAdminApi.createRoute(accessToken, payload);
      onCreated();
    } catch (err: unknown) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New route</CardTitle>
        <CardDescription>
          Directional: this describes the run from origin to destination
          only. The return leg is a separate route, because an empty
          backhaul is not the same commercial proposition.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={(e) => void submit(e)}>
          {error !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="route-code">Code</Label>
              <Input
                id="route-code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                placeholder="CHN-CBE"
                required
                disabled={submitting}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="route-name">Name</Label>
              <Input
                id="route-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="Chennai to Coimbatore"
                required
                disabled={submitting}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="route-origin">Origin</Label>
              <select
                id="route-origin"
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                value={originId}
                onChange={(e) => {
                  setOriginId(e.target.value);
                }}
                disabled={submitting}
              >
                <option value="">Select…</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.district} — {l.city}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="route-dest">Destination</Label>
              <select
                id="route-dest"
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                value={destId}
                onChange={(e) => {
                  setDestId(e.target.value);
                }}
                disabled={submitting}
              >
                <option value="">Select…</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.district} — {l.city}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="route-km">Standard distance (km)</Label>
              <Input
                id="route-km"
                inputMode="decimal"
                value={distanceKm}
                onChange={(e) => {
                  setDistanceKm(e.target.value);
                }}
                placeholder="495"
                required
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                The agreed commercial distance for rate cards and disputes.
                Trip ETAs still come from the routing service.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="route-toll">Toll (₹)</Label>
              <Input
                id="route-toll"
                inputMode="decimal"
                value={tollInr}
                onChange={(e) => {
                  setTollInr(e.target.value);
                }}
                placeholder="0"
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                0 for a toll-free lane.
              </p>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="route-notes">Notes (optional)</Label>
              <Input
                id="route-notes"
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
                disabled={submitting}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create route
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function RoutesPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [routes, setRoutes] = useState<MasterRoute[] | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) return;
      try {
        const rows = await masterdataAdminApi.listRoutes(accessToken, {}, signal);
        setRoutes(rows);
        setError(null);
      } catch (e: unknown) {
        if (signal?.aborted === true) return;
        setError(friendlyError(e));
        setRoutes([]);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  // Only ACTIVE locations are offered as endpoints. A route into a
  // deactivated city is a lane nobody can serve, and the FK would keep it
  // alive forever.
  useEffect(() => {
    if (accessToken === null) return;
    const controller = new AbortController();
    void masterdataAdminApi
      .listLocations(accessToken, { active: true }, controller.signal)
      .then(setLocations)
      .catch(() => {
        setLocations([]);
      });
    return () => {
      controller.abort();
    };
  }, [accessToken]);

  const toggleActive = async (r: MasterRoute): Promise<void> => {
    if (accessToken === null) return;
    setTogglingId(r.id);
    try {
      await masterdataAdminApi.updateRoute(accessToken, r.id, {
        is_active: !r.is_active,
      });
      await load();
    } catch (e: unknown) {
      setError(friendlyError(e));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Routes & tolls"
        description="Mastered lanes: the agreed distance and toll for a run between two locations. Directional — the return leg is its own route."
        actions={
          !creating && (
            <Button
              onClick={() => {
                setCreating(true);
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> New route
            </Button>
          )
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {creating && (
        <CreateRouteForm
          locations={locations}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
          onCancel={() => {
            setCreating(false);
          }}
        />
      )}

      <Card>
        <CardContent className="pt-6">
          {routes === null ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : routes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No routes yet. Loads on unmastered lanes still post normally —
              a route only adds an agreed distance and toll for pricing.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4">Code</th>
                    <th className="py-2 pr-4">Lane</th>
                    <th className="py-2 pr-4">Distance</th>
                    <th className="py-2 pr-4">Toll</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {routes.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{r.code}</td>
                      <td className="py-2 pr-4">
                        {r.origin_district ?? "—"} →{" "}
                        {r.dest_district ?? "—"}
                        <span className="block text-xs text-muted-foreground">
                          {r.name}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{r.standard_distance_km} km</td>
                      <td className="py-2 pr-4">₹{r.toll_inr}</td>
                      <td className="py-2 pr-4">
                        {r.is_active ? "Active" : "Retired"}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={togglingId === r.id}
                          onClick={() => void toggleActive(r)}
                        >
                          {togglingId === r.id && (
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          )}
                          {r.is_active ? "Retire" : "Reactivate"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
